import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ColumnDef } from '@tanstack/react-table';

import { EligibilityLimitOverrideDtoIn } from '@/models/in/EligibilityLimitOverrideDtoIn';
import DataTable from '@/components/ui/DataTable/DataTable';
import { Col } from '@/components/ui/DataTable/ColumnsHelpers';
import { useGetAuthApi } from '@/hooks/UseApiCalls';
import Button from '@/components/ui/Button';
import useTableDensity from '@/hooks/useTableDensity';
import DownloadFileButton from '@/components/ui/DownloadFileButton/DownloadFileButton';
import EditOrCreateOverrideDialog from './EditOrCreateOverrideDialog';
import DeleteOverrideDialog from './DeleteOverrideDialog';

const QUERY_URL = 'management/eligibility-limit-overrides';
const DENSITY_STORAGE_KEY = 'table.density.eligibilityOverrides';

const EligibilityOverridesManagementPage = () => {
  const { t } = useTranslation();

  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [overrideToEdit, setOverrideToEdit] = useState<EligibilityLimitOverrideDtoIn | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [overrideToDelete, setOverrideToDelete] = useState<EligibilityLimitOverrideDtoIn | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [density, setDensity] = useTableDensity(DENSITY_STORAGE_KEY, 'normal');

  const { data: overrides, isLoading, refetch } = useGetAuthApi<EligibilityLimitOverrideDtoIn[]>(QUERY_URL);

  const handleOpenCreateDialog = useCallback(() => {
    setOverrideToEdit(null);
    setIsEditDialogOpen(true);
  }, []);

  const handleOpenEditDialog = useCallback((override_: EligibilityLimitOverrideDtoIn) => {
    setOverrideToEdit(override_);
    setIsEditDialogOpen(true);
  }, []);

  const handleCloseEditDialog = useCallback(() => {
    setIsEditDialogOpen(false);
    setOverrideToEdit(null);
  }, []);

  const handleOpenDeleteDialog = useCallback((override_: EligibilityLimitOverrideDtoIn) => {
    setOverrideToDelete(override_);
    setIsDeleteDialogOpen(true);
  }, []);

  const handleCloseDeleteDialog = useCallback(() => {
    setIsDeleteDialogOpen(false);
    setOverrideToDelete(null);
  }, []);

  const columns: ColumnDef<EligibilityLimitOverrideDtoIn>[] = [
    {
      id: 'type',
      header: Col(t('eligibilityOverrides.type')),
      accessorFn: (row) => row.contactId ? t('eligibilityOverrides.contact') : t('eligibilityOverrides.fund'),
      enableSorting: true,
      meta: {
        className: 'min-w-[100px] max-w-[100px]',
      },
    },
    {
      id: 'limitType',
      header: Col(t('eligibilityOverrides.limitType')),
      accessorFn: (row) => row.limitType === 'Period'
        ? t('eligibilityOverrides.limitTypePeriod')
        : t('eligibilityOverrides.limitTypeMonthly'),
      enableSorting: true,
      meta: {
        className: 'min-w-[100px] max-w-[100px]',
      },
    },
    {
      id: 'fundNumber',
      header: Col(t('eligibilityOverrides.fundNumber')),
      // A contact override from before HAT-1477 has no fund and applies in all of them
      accessorFn: (row) => row.fundId ?? t('eligibilityOverrides.fundNotSet'),
      enableSorting: true,
      meta: {
        className: 'min-w-[110px] max-w-[110px]',
      },
    },
    {
      id: 'name',
      header: Col(t('eligibilityOverrides.name')),
      accessorFn: (row) => row.contactName ?? row.fundName ?? '',
      cell: ({ row }) => (
        <div className="flex flex-col">
          <span>{row.original.contactName ?? row.original.fundName}</span>
          {row.original.contactId && row.original.fundName && (
            <span className="body-xs-regular text-(--font-secondary)">{row.original.fundName}</span>
          )}
        </div>
      ),
      enableSorting: true,
      meta: {
        className: 'min-w-[200px]',
      },
    },
    {
      id: 'monthlyLimitAmount',
      header: Col(t('eligibilityOverrides.amount')),
      accessorKey: 'monthlyLimitAmount',
      cell: ({ getValue }) => {
        const value = getValue() as number;
        return <div>{value?.toLocaleString('he-IL', { style: 'currency', currency: 'ILS' })}</div>;
      },
      enableSorting: true,
      meta: {
        className: 'min-w-[130px] max-w-[130px]',
      },
    },
    {
      id: 'doubleOnHoliday',
      header: Col(t('eligibilityOverrides.doubleOnHoliday')),
      accessorKey: 'doubleOnHoliday',
      cell: ({ row, getValue }) => {
        if (row.original.limitType === 'Period') return <div>-</div>;
        const value = getValue() as boolean;
        return <div>{value ? t('eligibilityOverrides.yes') : t('eligibilityOverrides.no')}</div>;
      },
      enableSorting: false,
      meta: {
        className: 'min-w-[100px] max-w-[100px]',
      },
    },
    {
      id: 'expiresAt',
      header: Col(t('eligibilityOverrides.expiresAt')),
      accessorKey: 'expiresAt',
      cell: ({ row }) => {
        const { startsAt, expiresAt: dateStr, isExpired } = row.original;
        const formatted = new Date(dateStr).toLocaleDateString('he-IL');
        // A period override shows the whole period
        const period = row.original.limitType === 'Period' && startsAt
          ? `${new Date(startsAt).toLocaleDateString('he-IL')} - ${formatted}`
          : formatted;
        return (
          <span className={isExpired ? 'text-red-500' : ''}>
            {period}
            {isExpired && ` (${t('eligibilityOverrides.expired')})`}
          </span>
        );
      },
      enableSorting: true,
      meta: {
        className: 'min-w-[150px] max-w-[200px]',
      },
    },
    {
      id: 'comments',
      header: Col(t('eligibilityOverrides.comments')),
      accessorKey: 'comments',
      enableSorting: false,
      meta: {
        className: 'min-w-[200px]',
      },
    },
    {
      id: 'documents',
      header: Col(t('eligibilityOverrides.documents')),
      cell: ({ row }) => {
        const docs = row.original.documents;
        if (!docs || docs.length === 0) return <span>-</span>;
        return (
          <div className="flex flex-col gap-1">
            {docs.map((doc, idx) => (
              <div key={idx} className="flex items-center gap-1">
                <a
                  href={doc.uri}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="body-sm-regular text-blue-600 hover:text-blue-800 underline truncate"
                  onClick={(e) => e.stopPropagation()}
                >
                  {doc.name}
                </a>
                <DownloadFileButton uri={doc.uri} fileName={doc.name} />
              </div>
            ))}
          </div>
        );
      },
      enableSorting: false,
      meta: {
        className: 'min-w-[150px]',
      },
    },
    {
      id: 'actions',
      header: Col(t('eligibilityOverrides.actions')),
      enableSorting: false,
      cell: ({ row }) => (
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={(e) => {
              e.stopPropagation();
              handleOpenEditDialog(row.original);
            }}
          >
            {t('eligibilityOverrides.edit')}
          </Button>
          <Button
            variant="outline"
            className="!text-red-500 !border-red-300"
            onClick={(e) => {
              e.stopPropagation();
              handleOpenDeleteDialog(row.original);
            }}
          >
            {t('eligibilityOverrides.delete')}
          </Button>
        </div>
      ),
      meta: {
        className: 'min-w-[200px]',
        lockLayout: true,
      },
    },
  ];

  return (
    <div>
      <DataTable
        title={t('eligibilityOverrides.title')}
        data={overrides ?? []}
        isLoading={isLoading}
        columns={columns}
        filters={
          <Button variant="primary" onClick={handleOpenCreateDialog}>
            {t('eligibilityOverrides.addOverride')}
          </Button>
        }
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        density={density}
        onDensityChange={setDensity}
        columnLayoutKey="eligibilityOverrides"
        searchPlaceholder="eligibilityOverrides.search"
        customGlobalSearch={() => true}
      />

      <EditOrCreateOverrideDialog
        open={isEditDialogOpen}
        onClose={handleCloseEditDialog}
        overrideToEdit={overrideToEdit}
        onSuccess={refetch}
      />

      {isDeleteDialogOpen && overrideToDelete && (
        <DeleteOverrideDialog
          open={isDeleteDialogOpen}
          onClose={handleCloseDeleteDialog}
          override_={overrideToDelete}
          onSuccess={refetch}
        />
      )}
    </div>
  );
};

export default EligibilityOverridesManagementPage;
